import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Bell, Send, Phone, Calendar, AlertTriangle, CheckCircle, RefreshCw, User, Banknote } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Reminders = () => {
  const { user } = useAuth();
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingId, setSendingId] = useState(null);

  const fetchReminders = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/reminders/pending`);
      setReminders(response.data.reminders || []);
    } catch (error) {
      console.error('Error fetching reminders:', error);
      toast.error('Failed to load reminders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReminders();
  }, []);

  const handleSendReminder = async (subscriberId, subscriberName) => {
    setSendingId(subscriberId);
    try {
      const response = await axios.post(`${API}/reminders/send/${subscriberId}`);
      toast.success(response.data.message);
      // Optionally refresh list or mark as sent
      fetchReminders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reminder');
    } finally {
      setSendingId(null);
    }
  };

  const formatCurrency = (amount) => {
    return `₱${parseFloat(amount || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reminders-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Payment Reminders</h2>
          <p className="text-slate-600 mt-1">Send SMS reminders to overdue subscribers</p>
        </div>
        <Button 
          variant="outline" 
          onClick={fetchReminders}
          data-testid="refresh-btn"
        >
          <RefreshCw size={18} className="mr-2" />
          Refresh
        </Button>
      </div>

      {/* Summary Card */}
      <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-amber-100 text-sm">Pending Reminders</p>
            <p className="text-4xl font-bold mt-1">{reminders.length}</p>
            <p className="text-amber-100 text-sm mt-1">overdue subscribers</p>
          </div>
          <div className="p-4 bg-white/20 rounded-xl">
            <Bell size={40} />
          </div>
        </div>
      </div>

      {/* Reminders List */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-200 bg-slate-50">
          <h3 className="text-lg font-semibold text-slate-900">Overdue Subscribers</h3>
          <p className="text-sm text-slate-500">Click "Send" to send individual SMS reminders</p>
        </div>

        {reminders.length === 0 ? (
          <div className="p-12 text-center">
            <CheckCircle size={48} className="mx-auto mb-4 text-emerald-500" />
            <p className="text-slate-600 font-medium">No overdue subscribers!</p>
            <p className="text-slate-500 text-sm mt-1">All payments are up to date</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {reminders.map((reminder) => (
              <div 
                key={reminder.id} 
                className="p-4 hover:bg-slate-50 transition-colors"
                data-testid={`reminder-${reminder.id}`}
              >
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  {/* Subscriber Info */}
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-amber-100 rounded-lg">
                        <User size={20} className="text-amber-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">{reminder.name}</p>
                        <div className="flex items-center gap-2 text-sm text-slate-500">
                          <Phone size={14} />
                          <span>{reminder.mobile || 'No mobile'}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Details Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                      <div className="flex items-center gap-2">
                        <Banknote size={16} className="text-slate-400" />
                        <span className="text-slate-600">Amount:</span>
                        <span className="font-semibold text-slate-900">{formatCurrency(reminder.amount)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar size={16} className="text-slate-400" />
                        <span className="text-slate-600">Due:</span>
                        <span className="font-medium text-slate-900">{reminder.due_date}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <AlertTriangle size={16} className="text-red-500" />
                        <span className="text-red-600 font-semibold">{reminder.days_overdue} days overdue</span>
                      </div>
                    </div>

                    {/* Message Preview */}
                    <div className="mt-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                      <p className="text-xs text-slate-500 mb-1">SMS Message Preview:</p>
                      <p className="text-sm text-slate-700 whitespace-pre-line leading-relaxed">
                        {reminder.message}
                      </p>
                    </div>
                  </div>

                  {/* Send Button */}
                  <div className="flex-shrink-0">
                    <Button
                      onClick={() => handleSendReminder(reminder.id, reminder.name)}
                      disabled={sendingId === reminder.id || !reminder.mobile}
                      className="bg-blue-600 hover:bg-blue-700 w-full lg:w-auto"
                      data-testid={`send-reminder-${reminder.id}`}
                    >
                      {sendingId === reminder.id ? (
                        <>
                          <RefreshCw size={18} className="mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send size={18} className="mr-2" />
                          Send SMS
                        </>
                      )}
                    </Button>
                    {!reminder.mobile && (
                      <p className="text-xs text-red-500 mt-1 text-center">No mobile number</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Reminders;
